import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, MessageCircle, Instagram, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { CONTACT } from "@/lib/company";

// Envío de formulario sin backend: usa FormSubmit.co (gratuito, sin registro)
// para reenviar el mensaje directo al correo del negocio.
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${encodeURIComponent(CONTACT.email)}`;

const SERVICES = [
  "Preparación de impuestos",
  "ITIN Number (CAA)",
  "Servicios empresariales (LLC, EIN, Payroll)",
  "Contabilidad / Bookkeeping",
  "Formularios migratorios",
  "Notarización / Apostilla",
  "Seguros de vida / médicos",
  "Consulta general",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa nombre, email y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        FORM_ENDPOINT,
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          servicio: form.service,
          mensaje: form.message,
          _subject: `Nuevo contacto desde greatsolutionsus.com - ${form.name}`,
          _template: "table",
        },
        { headers: { Accept: "application/json" } }
      );
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Hubo un problema enviando tu mensaje. Intenta llamando directamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14 reveal">
          <div className="text-xs font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-4">
            Contáctanos
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0f172a] leading-[1.1]">
            Estamos aquí{" "}
            <span className="text-[#0B2D5C]">para ayudarte</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            Escríbenos, llámanos o visítanos. Respondemos con transparencia y rapidez.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: Contact info + Map */}
          <div className="lg:col-span-5 reveal space-y-6">
            <div className="border border-[#e2e8f0] rounded-sm overflow-hidden bg-[#f8fafc]">
              <div className="p-7 space-y-5">
                <a
                  href={`tel:${CONTACT.phone1Tel}`}
                  className="flex items-center gap-4 group"
                  data-testid="contact-phone-1"
                >
                  <div className="w-11 h-11 rounded-sm bg-[#0B2D5C]/8 group-hover:bg-[#0B2D5C] flex items-center justify-center transition-colors">
                    <Phone className="text-[#0B2D5C] group-hover:text-white transition-colors" size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs text-[#475569] uppercase tracking-wider">Teléfono principal</div>
                    <div className="font-semibold text-[#0f172a]">{CONTACT.phone1}</div>
                  </div>
                </a>

                <a
                  href={`tel:${CONTACT.phone2Tel}`}
                  className="flex items-center gap-4 group"
                  data-testid="contact-phone-2"
                >
                  <div className="w-11 h-11 rounded-sm bg-[#0B2D5C]/8 group-hover:bg-[#0B2D5C] flex items-center justify-center transition-colors">
                    <Phone className="text-[#0B2D5C] group-hover:text-white transition-colors" size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs text-[#475569] uppercase tracking-wider">Teléfono alternativo</div>
                    <div className="font-semibold text-[#0f172a]">{CONTACT.phone2}</div>
                  </div>
                </a>

                <a
                  href={`mailto:${CONTACT.email}`}
                  className="flex items-center gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-11 h-11 rounded-sm bg-[#0B2D5C]/8 group-hover:bg-[#0B2D5C] flex items-center justify-center transition-colors">
                    <Mail className="text-[#0B2D5C] group-hover:text-white transition-colors" size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs text-[#475569] uppercase tracking-wider">Email</div>
                    <div className="font-semibold text-[#0f172a] break-all">{CONTACT.email}</div>
                  </div>
                </a>

                <a
                  href={CONTACT.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                  data-testid="contact-instagram"
                >
                  <div className="w-11 h-11 rounded-sm bg-[#0B2D5C]/8 group-hover:bg-[#0B2D5C] flex items-center justify-center transition-colors">
                    <Instagram className="text-[#0B2D5C] group-hover:text-white transition-colors" size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs text-[#475569] uppercase tracking-wider">Instagram</div>
                    <div className="font-semibold text-[#0f172a]">@{CONTACT.instagram}</div>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-sm bg-[#0B2D5C]/8 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#0B2D5C]" size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-xs text-[#475569] uppercase tracking-wider">Dirección</div>
                    <div className="font-semibold text-[#0f172a]">{CONTACT.address}</div>
                    <div className="text-sm text-[#475569] mt-0.5">{CONTACT.addressExtra}</div>
                  </div>
                </div>

                <div className="pt-5 border-t border-[#e2e8f0]">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={14} className="text-[#0B2D5C]" strokeWidth={1.75} />
                    <div className="text-xs text-[#475569] uppercase tracking-wider font-semibold">Horario de atención</div>
                  </div>
                  <ul className="space-y-1 text-sm">
                    {CONTACT.hours.map((h) => (
                      <li key={h.day} className="flex justify-between">
                        <span className="text-[#475569]">{h.day}</span>
                        <span className="font-medium text-[#0f172a]">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=Hola%20Yohaly%2C%20me%20gustar%C3%ADa%20agendar%20una%20consulta`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold py-4 transition-colors"
                data-testid="contact-whatsapp"
              >
                <MessageCircle size={20} />
                Escríbenos por WhatsApp
              </a>
            </div>

            {/* Google Map embed */}
            <div className="rounded-sm overflow-hidden border border-[#e2e8f0] h-[240px]" data-testid="contact-map">
              <iframe
                title="Ubicación Great Solutions US"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src="https://www.google.com/maps?q=12555+Orange+Dr,+Suite+4283,+Davie,+FL+33330&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7 reveal">
            <div className="bg-[#f8fafc] border border-[#e2e8f0] p-8 lg:p-10 rounded-sm">
              <h3 className="font-display text-2xl font-bold text-[#0f172a] mb-2">Solicita una consulta gratuita</h3>
              <p className="text-sm text-[#475569] mb-7">
                Cuéntanos brevemente tu caso y te contactaremos pronto con la mejor solución.
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-[#0f172a] font-medium">Nombre completo *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Tu nombre"
                      className="mt-2 h-12 bg-white border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#0f172a] font-medium">Teléfono</Label>
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(786) 000-0000"
                      className="mt-2 h-12 bg-white border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                      data-testid="contact-input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#0f172a] font-medium">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="tu@email.com"
                    className="mt-2 h-12 bg-white border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                    data-testid="contact-input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-[#0f172a] font-medium">Servicio de interés</Label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger
                      className="mt-2 h-12 bg-white border-[#e2e8f0] rounded-sm focus:ring-[#0B2D5C]"
                      data-testid="contact-select-service"
                    >
                      <SelectValue placeholder="Selecciona un servicio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0f172a] font-medium">Cuéntanos tu caso *</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Describe brevemente en qué podemos ayudarte..."
                    rows={5}
                    className="mt-2 bg-white border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C] resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm text-base font-medium"
                  data-testid="contact-submit"
                >
                  {loading ? "Enviando..." : (
                    <>
                      Enviar mensaje
                      <Send size={16} className="ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-[#475569] text-center">
                  Al enviar aceptas ser contactado por Great Solutions US. Tu información es confidencial.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
